// Volt Box visual language — matches /12: deep night background, Playfair headings, gold + cyan accents.
import { useEffect, useRef, useState } from "react";

export const INPUT = "w-full rounded-xl bg-white/[0.05] border border-white/[0.12] px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2CC8F]/60 focus:ring-2 focus:ring-[#F2CC8F]/15 transition-colors";
const BTN = {
  gold: "bg-[#F2CC8F] text-[#080E14] px-5 py-2.5 text-sm font-black hover:brightness-110 shadow-[0_8px_30px_-12px_rgba(242,204,143,0.6)]",
  ghost: "border border-white/15 text-white/85 px-4 py-2 text-sm font-bold hover:bg-white/[0.06] hover:border-white/25",
  quiet: "px-3 py-1.5 text-xs font-bold text-white/60 hover:text-white hover:bg-white/[0.06]",
  danger: "border border-[#FF6B6B]/30 text-[#FF9A9A] px-4 py-2 text-sm font-bold hover:bg-[#FF6B6B]/10",
  cyan: "bg-[#54E3FF] text-[#080E14] px-5 py-2.5 text-sm font-black hover:brightness-110",
};

export const Btn = ({ variant = "gold", className = "", children, ...p }) => (
  <button type="button" className={`inline-flex items-center justify-center gap-2 rounded-full transition-[filter,background-color,border-color,transform] active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none ${BTN[variant]} ${className}`} {...p}>{children}</button>
);

export const Card = ({ className = "", children, ...p }) => (
  <div className={`rounded-2xl bg-white/[0.04] border border-white/[0.08] ${className}`} {...p}>{children}</div>
);

export const Eyebrow = ({ children, color = "#F2CC8F", className = "" }) => (
  <p className={`text-[10px] font-black uppercase tracking-[0.3em] ${className}`} style={{ color }}>{children}</p>
);

export const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); o.disconnect(); } }, { threshold: 0.08 });
    o.observe(el);
    return () => o.disconnect();
  }, []);
  return <div ref={ref} className={`transition-[opacity,transform] duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

export const Section = ({ id, step, eyebrow, title, sub, accent = "#F2CC8F", right, children }) => (
  <section id={id} className="scroll-mt-28 py-10 sm:py-14 border-t border-white/[0.06]" data-testid={`vb-section-${id}`}>
    <FadeIn>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <Eyebrow color={accent} className="mb-2">{step && <span className="mr-2 text-white/30">{step}</span>}{eyebrow}</Eyebrow>
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold leading-tight text-white">{title}</h2>
          {sub && <p className="mt-2 text-sm text-white/55 leading-relaxed">{sub}</p>}
        </div>
        {right}
      </div>
    </FadeIn>
    <div className="mt-6">{children}</div>
  </section>
);

export const Toggle = ({ on, onChange, label, hint, testid }) => (
  <button type="button" onClick={() => onChange(!on)} className="flex items-start gap-3 text-left group" data-testid={testid} aria-pressed={on}>
    <span className={`mt-0.5 h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors ${on ? "bg-[#F2CC8F]" : "bg-white/15"}`}>
      <span className={`block h-4 w-4 rounded-full bg-[#080E14] transition-transform ${on ? "translate-x-4" : ""}`} />
    </span>
    <span>
      <span className="block text-sm font-bold text-white/90">{label}</span>
      {hint && <span className="block text-[11px] text-white/45 leading-snug mt-0.5">{hint}</span>}
    </span>
  </button>
);

export const Spinner = ({ className = "h-4 w-4" }) => <span className={`inline-block rounded-full border-2 border-white/20 border-t-[#F2CC8F] animate-spin ${className}`} aria-hidden />;

export const useTwoTap = (ms = 4000) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => { if (!armed) return undefined; const t = setTimeout(() => setArmed(false), ms); return () => clearTimeout(t); }, [armed, ms]);
  return [armed, () => { if (armed) { setArmed(false); return true; } setArmed(true); return false; }];
};
